import React, { useState } from 'react';
import { X, ChevronDown, ChevronRight, SquareTerminal, RefreshCcwDot, ChartPie, Webhook, Cable, Boxes, File, Mail, SquareCheckBig, Database, DatabaseBackup, Folder, Search, Bot, Brain, Eye } from 'lucide-react';
import { useMapBridge } from '../../../shared/store/useMapBridge';
import './MapTools.css';

const AssetCategory = ({ title, items, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    if (!items || items.length === 0) return null;

    return (
        <div className="asset-category" style={{ marginBottom: 'var(--space-6)' }}>
            <div 
                className="asset-category-header" 
                onClick={() => setExpanded(!expanded)}
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer', 
                    color: '#aaa', 
                    marginBottom: 'var(--space-3)',
                    userSelect: 'none'
                }}
            >
                {expanded ? <ChevronDown size={16} style={{ marginRight: 'var(--space-2)' }}/> : <ChevronRight size={16} style={{ marginRight: 'var(--space-2)' }}/>}
                <h4 className="asset-category-title" style={{ margin: 0, flex: 1, fontSize: '14px', fontWeight: 'bold' }}>{title}</h4>
                <span className="asset-count-tag" style={{ background: '#222', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-lg)', fontSize: '11px', color: '#888' }}>
                    {items.length}
                </span>
            </div>
            
            {expanded && (
                <div className="asset-list" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginLeft: 'var(--space-6)' }}>
                    {items.map((item, idx) => (
                        <button 
                            key={idx}
                            className={`asset-item-btn ${item.disabled ? 'disabled' : ''}`}
                            onClick={item.disabled ? null : item.onClick}
                            style={{ 
                                padding: 'var(--space-3)', 
                                background: item.disabled ? '#1a1a1a' : '#222', 
                                border: 'none', 
                                color: item.disabled ? '#777' : '#fff', 
                                borderRadius: 'var(--radius-md)', 
                                cursor: item.disabled ? 'not-allowed' : 'pointer', 
                                textAlign: 'left',
                                fontSize: '13px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                width: '100%'
                            }}
                        >
                            {item.icon && <item.icon size={16} />}
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const MapTools = ({ onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    // active-terminal-change / window.spawnMapAsset (paso a3): antes un CustomEvent + una
    // función global de `window`, ahora un selector + una acción de `useMapBridge`.
    const activeTerminalId = useMapBridge((s) => s.activeTerminalId);

    const handleSpawnAdminTerminal = () => {
        const spawnAsset = useMapBridge.getState().spawnAsset;
        if (typeof spawnAsset === 'function') {
            spawnAsset('admin_terminal', 'Usuario Admin', 150, 100);
        } else {
            console.error('useMapBridge spawnAsset is not available.');
        }
    };

    const terminalItems = [
        { label: 'usuario admin', icon: SquareTerminal, onClick: handleSpawnAdminTerminal },
        { label: 'usuario scrum', icon: SquareTerminal, onClick: () => useMapBridge.getState().spawnAsset('usuario_scrum', 'Usuario Scrum', 150, 100) },
        { label: 'usuario', icon: SquareTerminal, onClick: () => useMapBridge.getState().spawnAsset('usuario', 'Usuario', 150, 100) },
        { label: 'multiples usuarios', icon: SquareTerminal, onClick: () => useMapBridge.getState().spawnAsset('multiples_usuarios', 'Múltiples Usuarios', 150, 100) }
    ];

    const sistemasItems = activeTerminalId ? [
        { label: 'cronjobs', icon: RefreshCcwDot, disabled: true },
        { label: 'panel de metricas', icon: ChartPie, disabled: true },
        { label: 'consola', icon: ChartPie, disabled: false, onClick: () => useMapBridge.getState().spawnAsset('consola', 'Consola', 176, 130, 1, activeTerminalId) },
        { label: 'webhook', icon: Webhook, disabled: true },
        { label: 'api service', icon: Cable, disabled: true },
        { label: 'prop', icon: Cable, disabled: true },
        { label: 'ia', icon: Cable, disabled: true },
        // Integración main→dev epic C4: agentes de mapa dentro de un terminal (nivel 1).
        { label: ' worker', icon: Bot, onClick: () => useMapBridge.getState().spawnAsset('agent_worker', 'Worker', 50, 50, 1, activeTerminalId) },
        { label: ' planeación', icon: Brain, onClick: () => useMapBridge.getState().spawnAsset('agent_planeacion', 'Planeación', 50, 50, 1, activeTerminalId) },
        { label: ' supervisión', icon: Eye, onClick: () => useMapBridge.getState().spawnAsset('agent_supervision', 'Supervisión', 50, 50, 1, activeTerminalId) },
        { label: 'wallet', icon: Boxes, disabled: true }
    ] : [];

    const datosItems = activeTerminalId ? [
        { label: 'documentos', icon: File, disabled: true },
        { label: 'correos', icon: Mail, disabled: true },
        { label: 'tareas', icon: SquareCheckBig, disabled: true },
        { label: 'base de datos', icon: Database, disabled: true },
        { label: 'respaldos', icon: DatabaseBackup, disabled: true },
        { label: 'archivos', icon: Folder, disabled: true }
    ] : [];

    const filterItems = (items) => {
        if (!searchTerm) return items;
        const lowerSearch = searchTerm.toLowerCase();
        return items.filter(item => item.label.toLowerCase().includes(lowerSearch));
    };

    return (
        <div className="maptools-panel-container">
            <div className="maptools-panel-header">
                <h3>Biblioteca de Assets</h3>
                <button className="maptools-close-btn" onClick={onClose} title="Cerrar (map-floating-close)" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}>
                    <X size={20} color="#FFF" />
                </button>
            </div>
            

            <div style={{ padding: 'var(--space-4) var(--space-4) 0 var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#111', border: '1px solid #333', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)' }}>
                    <Search size={16} color="#888" style={{ marginRight: 'var(--space-2)' }} />
                    <input
                        type="text"
                        placeholder="Buscar assets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ background: 'transparent', border: 'none', color: '#FFF', outline: 'none', width: '100%', fontSize: '13px' }}
                    />
                </div>
            </div>

            <div className="decor-panel-v2" style={{ flexDirection: 'column', padding: 'var(--space-4)', overflowY: 'auto' }}>
                <AssetCategory title="Terminales" items={filterItems(terminalItems)} defaultExpanded={true} />
                <AssetCategory title="Sistemas" items={filterItems(sistemasItems)} defaultExpanded={true} />
                <AssetCategory title="Datos" items={filterItems(datosItems)} defaultExpanded={true} />
            </div>
        </div>
    );
};

export default MapTools;
